import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Loader } from "@jti/core-components/components/Loader";
import { Tooltip, Txt, useTranslations } from "@jti/ui";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";

import "./OrderLoyaltyConfirmation.scss";

const { sanitize } = dompurify;

type OrderLoyaltyConfirmationProps = {
  totalPoints: number | undefined;
  pendingPoints: number | undefined;
  earnedPoints: number | undefined;
  arrowUrl?: string;
  orderFetchStatus?: string;
};

export const OrderLoyaltyConfirmation: FC<OrderLoyaltyConfirmationProps> = ({
  totalPoints,
  earnedPoints,
  pendingPoints,
  arrowUrl,
  orderFetchStatus,
}) => {
  const { __ } = useTranslations();

  if (orderFetchStatus === "pending") {
    return <Loader />;
  }

  return (
    <div className="reward-info">
      <Card variant="level-1" title={__("cart.checkout.reward_banner.title")}>
        <div className={"reward-banner"}>
          <div className={"reward-banner__content"}>
            <LoyaltyLogo className={"reward-banner__logo"} />
            <Txt className={"reward-banner__message"}>
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(__("cart.checkout.reward_banner.message", [earnedPoints || 0])),
                }}
                data-testid={"cart-checkout-reward_banner"}
              ></span>
            </Txt>
          </div>
        </div>
      </Card>
      <div className="reward-points">
        <Card variant="level-1-club" title={__("cart.checkout.reward_banner.points_balance.title")}>
          <Txt className="reward-points__content">
            <span className="reward-points__balance" data-testid="cart-checkout-reward_balance">
              {totalPoints}
            </span>
            <div className="reward-points__pending">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitize(__("cart.checkout.reward_banner.points_pending", [pendingPoints || 0])),
                }}
                className="reward-points__text"
                data-testid="cart-checkout-reward_pending-points"
              ></span>
              <Tooltip
                content={
                  <>
                    <div>{__("tooltip.text")}</div>
                    <div
                      className="reward-points__tooltip-link"
                      dangerouslySetInnerHTML={{ __html: sanitize(__("tooltip.text.url-text")) }}
                    ></div>
                  </>
                }
              />
            </div>
            <Button
              variant="text-muted"
              size="sm"
              data-testid="arrow-link"
              href={arrowUrl}
              nativeBehaviour={true}
              iconAfter={LongArrowForwardIcon}
            >
              {__("cart.checkout.reward_banner.points_history")}
            </Button>
          </Txt>
        </Card>
      </div>
    </div>
  );
};
